import { readonly, ref } from "vue";
import { fetchCurrentUser, loginUser, logoutUser, registerUser, updateProfile, uploadAvatar } from "@/services/api/authApi";
import { ApiRequestError } from "@/services/api/httpClient";
import type { AuthUser, LoginPayload, RegisterPayload } from "@/types/auth";

const currentUser = ref<AuthUser | null>(null);
const initialized = ref(false);
const loading = ref(false);
const authPanelOpen = ref(false);

export function useAuth() {
  async function restore(): Promise<void> {
    if (initialized.value || loading.value) return;
    loading.value = true;
    try {
      currentUser.value = await fetchCurrentUser();
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.status !== 401) {
        console.warn("[Auth] Failed to restore session", error);
      }
      currentUser.value = null;
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }

  async function login(payload: LoginPayload): Promise<AuthUser> {
    loading.value = true;
    try {
      currentUser.value = await loginUser(payload);
      initialized.value = true;
      return currentUser.value;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload: RegisterPayload): Promise<AuthUser> {
    loading.value = true;
    try {
      currentUser.value = await registerUser(payload);
      initialized.value = true;
      return currentUser.value;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    loading.value = true;
    try {
      await logoutUser();
      currentUser.value = null;
      initialized.value = true;
    } finally {
      loading.value = false;
    }
  }

  async function updateDisplayName(displayName: string): Promise<AuthUser> {
    loading.value = true;
    try {
      currentUser.value = await updateProfile(displayName);
      return currentUser.value;
    } finally {
      loading.value = false;
    }
  }

  async function updateUserAvatar(file: File): Promise<AuthUser> {
    loading.value = true;
    try {
      currentUser.value = await uploadAvatar(file);
      return currentUser.value;
    } finally {
      loading.value = false;
    }
  }

  function openAuthPanel(): void {
    authPanelOpen.value = true;
  }

  function closeAuthPanel(): void {
    authPanelOpen.value = false;
  }

  return {
    currentUser: readonly(currentUser),
    initialized: readonly(initialized),
    loading: readonly(loading),
    authPanelOpen: readonly(authPanelOpen),
    restore,
    login,
    register,
    logout,
    updateDisplayName,
    updateUserAvatar,
    openAuthPanel,
    closeAuthPanel
  };
}
